import { ActivityDTO } from './dto/ActivityDTO';
import { api } from 'agent';
import Connector from '../../agent';
import { QueryKey } from '../QueryKey';
import { RiskAssessmentDTO } from './dto/RiskAssessmentDTO';
import { ActionDTO } from './dto/ActionDTO';
import { ActivitiesViewDTO } from './dto/ActivitiesViewDTO';
import { ActivityCloneDTO } from './dto/ActivityCloneDTO';
import { ActionsResponse } from '@components/Activity/types/ActionsRepsonse';
import { ActionCommentDTO } from './dto/ActionCommentDTO';
import { ActivityInitialDataResponse } from '@components/Activity/types/ActivityInitialDataResponse';
import { ActivityTraceabilityDTO } from './dto/ActivityTraceabilityDTO';
import { LocationDTO } from '@behavior/inventories/dto/LocationDTO';
import { ContactDTO } from '@behavior/inventories/dto/ContactDTO';

export function getActivity(activityId: number): Promise<ActivityDTO> {
  return api
    .get(`${QueryKey.ACTIVITY}/${activityId}`)
    .then((response: { data: ActivityDTO }) => response.data);
}

export function getAction(actionId: number): Promise<ActionDTO> {
  return api
    .get(`${QueryKey.ACTION}/${actionId}`)
    .then((response: { data: ActionDTO }) => response.data);
}

export function getActivityInitialData(
  isCreationMode: boolean
): Promise<ActivityInitialDataResponse> {
  return api
    .get(`${QueryKey.ACTIVITY}/getActivityData?isCreationMode=${isCreationMode}`)
    .then((response: { data: ActivityInitialDataResponse }) => response.data);
}

export function postActivity(activity: ActivityDTO): Promise<ActivityDTO> {
  return api
    .post(`${QueryKey.ACTIVITY}`, { ...activity })
    .then((response: { data: ActivityDTO }) => response.data);
}

export function postComment(actionId: number, comment: ActionCommentDTO): Promise<ActionDTO> {
  return api
    .post(`${QueryKey.ACTION}/${actionId}/addComment`, { ...comment })
    .then((response: { data: ActionDTO }) => response.data);
}

export function postCloneActivity(activity: ActivityCloneDTO): Promise<ActivityCloneDTO> {
  return api
    .post(`${QueryKey.ACTIVITY}/clone`, activity)
    .then((response: { data: ActivityCloneDTO }) => response.data);
}

export function postRiskAssessment(riskAssessment: RiskAssessmentDTO): Promise<RiskAssessmentDTO> {
  return api
    .post(`${QueryKey.RISK_ASSESSMENT}`, { ...riskAssessment })
    .then((response: { data: RiskAssessmentDTO }) => response.data);
}

export function getRiskAssessmentInitialData(isCreationMode: boolean): Promise<RiskAssessmentDTO> {
  return api
    .get(`${QueryKey.RISK_ASSESSMENT}/getRiskAssessmentInitial?isCreationMode=${isCreationMode}`)
    .then((response: { data: RiskAssessmentDTO }) => response.data);
}

export function getActionInitialData(): Promise<ActionDTO> {
  return api
    .get(`${QueryKey.ACTION}/getActionInitial`)
    .then((response: { data: ActionDTO }) => response.data);
}

export function postActionList(actionList: ActionDTO[]): Promise<void> {
  return api.post(`${QueryKey.ACTION}/saveActionList`, actionList);
}

export function getAllActions(): Promise<ActionsResponse> {
  return api
    .get(`${QueryKey.ACTION}/actions`)
    .then((response: { data: ActionsResponse }) => response.data);
}

export function getTraceabilityInitial(): Promise<ActivityTraceabilityDTO> {
  return api
    .get(`${QueryKey.TRACEABILITY}/${QueryKey.GET_TRACEABILITY_INITIAL}`)
    .then((response: { data: ActivityTraceabilityDTO }) => response.data);
}

export function postTraceability(
  traceability: ActivityTraceabilityDTO
): Promise<ActivityTraceabilityDTO> {
  return api.post(`${QueryKey.TRACEABILITY}`, traceability);
}

export function deleteActivity(activityId: number): Promise<void> {
  return api.delete(`${QueryKey.ACTIVITY}/${activityId}`);
}

export function releaseActivity(activityId: number): Promise<ActivityDTO> {
  return api
    .post(`${QueryKey.ACTIVITY}/${activityId}/${QueryKey.RELEASE_ACTIVITY}`)
    .then((response: { data: ActivityDTO }) => response.data);
}

export function releaseActions(activityId: number): Promise<ActivityDTO> {
  return api
    .post(`${QueryKey.ACTIVITY}/${activityId}/${QueryKey.RELEASE_ACTIONS}`)
    .then((response: { data: ActivityDTO }) => response.data);
}

export function markActionAsCompleted(actionId: number): Promise<ActionDTO> {
  return api
    .get(`${QueryKey.ACTION}/complete/${actionId}`)
    .then((response: { data: ActionDTO }) => response.data);
}

export function getPageableActivities(
  pageSize?: number,
  page?: number,
  sortingColumn?: string,
  sortingDirection?: string,
  filterValue?: string
): Promise<ActivitiesViewDTO> {
  let url = `${QueryKey.ACTIVITY_VIEW}`;

  if (pageSize || page || sortingColumn || filterValue) {
    url += '?';

    if (pageSize && typeof pageSize === 'number') {
      url += `pageSize=${pageSize}`;
    }
    if (page && typeof page === 'number') {
      url += `${pageSize ? '&' : ''}page=${page}`;
    }
    if (
      sortingColumn &&
      typeof sortingColumn === 'string' &&
      sortingDirection &&
      typeof sortingDirection === 'string'
    ) {
      url += `${
        pageSize || page ? '&' : ''
      }sortingColumn=${sortingColumn}&sortingDirection=${sortingDirection}`;
    }
    if (filterValue && typeof filterValue === 'string') {
      url += `${pageSize || page || sortingColumn ? '&' : ''}filterValues=${filterValue}`;
    }
  }

  return api.get(url).then((response: { data: ActivitiesViewDTO }) => response.data);
}

export function getDepartment(): Promise<string[]> {
  return api
    .get(`${QueryKey.ACTIVITY_VIEW}/getDepartment`)
    .then((response: { data: string[] }) => response.data);
}

export function downloadActivityReport(activityId: number): Promise<Blob> {
  return Connector.api.pdf(
    `${QueryKey.REPORTS}/${QueryKey.ACTIVITY_PDF}/${activityId}`,
    null,
    {},
    'ActivityReport.pdf',
    'post',
    {}
  );
}

export function getDistributionListPreFill(locations: LocationDTO[]): Promise<ContactDTO[]> {
  return api
    .post(`${QueryKey.TRACEABILITY}/getDistributionListPreFill`, locations)
    .then((response: { data: ContactDTO[] }) => response.data);
}
